import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMembersQuery } from '@/api/query/user';
import { useAppContext } from '@/contexts/AppContext';
import { useMemberContext } from '@/contexts/MemberContext';
import { usePaymentContext } from '@/contexts/PaymentContext';
import type { Member } from '@/types';
import { Plus, Search, Download } from 'lucide-react';
import { MemberTable } from './MemberTable';
import { ViewMember } from './ViewMember';
import { UpsertMember } from './UpsertMember';
import { createColumns, type ColumnSort, type SortField } from './TableColumns';

export default function MemberManagement() {
  const { isAuthenticated } = useAppContext();
  // Subscribe here so leaving/returning to this tab refetches in the background.
  useMembersQuery(isAuthenticated);

  const { members, isLoading, isFetching, error } = useMemberContext();
  const { getManagementByMemberId } = usePaymentContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isEditMemberOpen, setIsEditMemberOpen] = useState(false);
  const [isViewMemberOpen, setIsViewMemberOpen] = useState(false);
  const [sort, setSort] = useState<ColumnSort | null>({ field: 'id', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Filter members based on search term
  const filteredMembers = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return members.filter(member => 
      member.id.includes(searchTerm) ||
      member.name.toLowerCase().includes(lowerSearchTerm) ||
      member.phone.includes(searchTerm)
    );
  }, [members, searchTerm]);

  // Sort members
  const sortedMembers = useMemo(() => {
    if (!sort) return filteredMembers;

    const sorted = [...filteredMembers].sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case 'id':
          comparison = a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' });
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'email':
          comparison = (a.email ?? '').localeCompare(b.email ?? '');
          break;
        case 'phone':
          comparison = a.phone.localeCompare(b.phone);
          break;
        case 'joinedDate':
          comparison = new Date(a.joinedDate).getTime() - new Date(b.joinedDate).getTime();
          break;
        case 'endDate':
          comparison = new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
          break;
        default:
          return 0;
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredMembers, sort]);

  // Paginate members
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedMembers.slice(startIndex, endIndex);
  }, [sortedMembers, currentPage, pageSize]);

  // Reset to page 1 when search or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sort]);

  const handleSort = useCallback((field: SortField) => {
    setSort((currentSort) => {
      if (currentSort?.field === field) {
        // Toggle direction if same field
        return {
          field,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      // New field, default to ascending
      return { field, direction: 'asc' };
    });
  }, []);

  const handleViewMember = useCallback((member: Member) => {
    setSelectedMember(member);
    setIsViewMemberOpen(true);
  }, []);

  const handleEditMember = useCallback((member: Member) => {
    setSelectedMember(member);
    setIsEditMemberOpen(true);
  }, []);

  const handleEditFromView = useCallback((member: Member) => {
    setSelectedMember(member);
    setIsViewMemberOpen(false);
    setIsEditMemberOpen(true);
  }, []);

  const getMonthsPaid = useCallback(
    (memberId: string) => getManagementByMemberId(memberId)?.paid_months ?? 0,
    [getManagementByMemberId]
  );

  const exportMembers = useCallback(() => {
    // Simple CSV export simulation
    const csvData = [
      ['Name', 'Phone', 'Email', 'Joined Date', 'End Date', 'Address'],
      ...sortedMembers.map(member => [
        member.name,
        member.phone,
        member.email,
        member.joinedDate,
        member.endDate,
        member.address
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'members_report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }, [sortedMembers]);

  return (
    <div className="h-full flex flex-col p-5">
      {/* Header */}
      <div className="mb-6 flex-shrink-0">
        <div className="mb-4 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">Member Management</h1>
              {isFetching && !isLoading && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#4369a0]/25 border-t-[#4369a0]" />
              )}
            </div>
            <p className="mt-0.5 text-sm text-gray-500">Manage all registered members</p>
          </div>
          <Button 
            onClick={() => setIsAddMemberOpen(true)}
            className="bg-[#4369a0] text-white hover:bg-[#4369a0]/90"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Member
          </Button>
        </div>
        <div className="h-px bg-gray-200/80" />
      </div>

      {/* Members Table */}
      <Card className="flex min-h-0 flex-1 flex-col border-gray-200/80 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col p-5">
          <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-3">
            <h2 className="whitespace-nowrap text-sm font-semibold text-gray-900">
              All Members
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {sortedMembers.length}
              </span>
            </h2>
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by ID, name, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 border-gray-200 bg-white pl-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={exportMembers}
              className="h-9 whitespace-nowrap border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-gray-200/80 bg-white">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#4369a0]/30 border-t-[#4369a0]" />
                <p className="mt-3 text-sm text-gray-500">Loading members...</p>
              </div>
            ) : error ? (
              <div className="flex h-full flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50/60 px-6 text-center">
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            ) : (
              <MemberTable
                members={paginatedMembers}
                columns={createColumns(sort, handleSort, handleViewMember, handleEditMember, getMonthsPaid)}
                sort={sort}
                onSort={handleSort}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={sortedMembers.length}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <UpsertMember member={null} open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen} />

      <ViewMember
        member={selectedMember}
        open={isViewMemberOpen}
        onOpenChange={setIsViewMemberOpen}
        onEdit={handleEditFromView}
      />

      <UpsertMember
        member={selectedMember}
        open={isEditMemberOpen}
        onOpenChange={setIsEditMemberOpen}
      />
    </div>
  );
}

